import { Component, effect, input } from '@angular/core';
import {
  MaintenanceCriteriaFullModel,
  MasteryCriteriaModel,
} from '../../core/api/models';
import { SupervisorApiService } from '../../core/api/services';
import { AlertService } from '../../shared/services/alert.service';
import { SettingsHelperService } from '../settings.helper.service';
import { Utils } from '../../core/utils';
import { forkJoin } from 'rxjs';
import { getPatientTargetStatuses } from '../../shared/utils/utils';

@Component({
  selector: 'app-mastery-criteria',
  templateUrl: './mastery-criteria.component.html',
  styleUrl: './mastery-criteria.component.css',
  standalone: false,
})
export class MasteryCriteriaComponent {
  patientID = input<string | undefined>();

  masteryCriteria: MasteryCriteriaModel = {};
  targetStatuses: any[] = [];
  loadingMasteryCriteria: boolean = false;
  hasCustomMasteryCriteria: boolean = false;
  isScreenForSinglePatient: boolean = false;
  shouldShowResetToGeneralSettings: boolean = false;

  constructor(
    private supervisorApiService: SupervisorApiService,
    private alertService: AlertService,
    protected settingsHelperService: SettingsHelperService
  ) {
    effect(() => {
      console.log(
        'update signal triggered: ',
        this.settingsHelperService.updateSignal()
      );
      this.loadData();
    });
  }

  ngOnInit(): void {
    console.log('MaintananceCriteriaComponent ngOnInit');
    this.loadData();
  }

  loadData() {
    this.loadingMasteryCriteria = true;
    const patinetIdToUse =
      this.patientID() && !isNaN(Number(this.patientID()))
        ? Number(this.patientID())
        : undefined;
    if (patinetIdToUse) {
      this.isScreenForSinglePatient = true;
      this.hasCustomMasteryCriteria = false;
    }

    const GetMasteryCriteriaObservable =
      this.supervisorApiService.GetMasteryCriteria({
        patientID: patinetIdToUse,
      });

    this.targetStatuses = getPatientTargetStatuses();

    GetMasteryCriteriaObservable.subscribe(
      (masteryCriteria) => {
        if (masteryCriteria) {
          this.hasCustomMasteryCriteria = true;
          this.masteryCriteria = masteryCriteria;
          this.shouldShowResetToGeneralSettings = true;
        } else {
          // Load general settings as default values
          this.loadGeneralSettingsAsDefault(patinetIdToUse);
          this.shouldShowResetToGeneralSettings = false;
        }
        this.loadingMasteryCriteria = false;
      },
      (err) => {
        this.loadingMasteryCriteria = false;
        this.alertService.error(Utils.getErrorMessageFromErrorResponse(err));
      }
    );
  }

  save() {
    this.loadingMasteryCriteria = true;
    this.supervisorApiService
      .SaveMasteryCriteria({ body: this.masteryCriteria })
      .subscribe(
        () => {
          this.loadingMasteryCriteria = false;
          this.hasCustomMasteryCriteria = true;
          this.shouldShowResetToGeneralSettings = true;
          this.alertService.success('Mastery criteria saved successfully');
        },
        (err) => {
          this.loadingMasteryCriteria = false;
          this.alertService.error(Utils.getErrorMessageFromErrorResponse(err));
        }
      );
  }

  enableCustomMasteryCriteria() {
    this.save();
  }

  loadGeneralSettingsAsDefault(patientID: number | undefined): void {
    this.supervisorApiService.GetMasteryCriteria({
      patientID: undefined
    }).subscribe({
      next: (generalMasteryCriteria) => {
        if (generalMasteryCriteria) {
          this.masteryCriteria = {
            ...generalMasteryCriteria,
            PatientID: patientID,
            ID: undefined // Clear ID so it's treated as new custom settings when saved
          };
        } else {
          this.masteryCriteria = { PatientID: patientID };
        }
      },
      error: (err) => {
        this.alertService.error(Utils.getErrorMessageFromErrorResponse(err));
        this.masteryCriteria = { PatientID: patientID };
      }
    });
  }

  resetToGeneralSettings() {
    this.supervisorApiService.RemoveCustomMasteryCriteria({ body: this.masteryCriteria }).subscribe({
      next: () => {
        this.alertService.success('Mastery criteria reset to general settings successfully');
        this.loadData();
      },
      error: (err) => {
        this.alertService.error(Utils.getErrorMessageFromErrorResponse(err));
      }
    });
  }

  get pageModeLabel(): string {
    if (!this.isScreenForSinglePatient) {
      return 'General Mastery Defaults';
    }

    return this.hasCustomMasteryCriteria
      ? 'Patient-Specific Mastery Defaults'
      : 'Using General Mastery Defaults';
  }

  get pageSummary(): string {
    if (!this.isScreenForSinglePatient) {
      return 'These mastery criteria are used when there is no patient-specific criteria.';
    }

    if (this.hasCustomMasteryCriteria) {
      return 'These patient-specific mastery criteria override the general defaults for this patient.';
    }

    return 'This patient is currently inheriting the general mastery criteria settings.';
  }

  get generalSettingsLinkLabel(): string {
    return 'general mastery criteria';
  }

  get responsePercentageHint(): string {
    return 'Spark uses this percentage threshold together with the other criteria to determine whether automatic mastery is met.';
  }

  get consecutiveDaysHint(): string {
    return 'This is the number of consecutive sessions or days that must meet the configured mastery criteria.';
  }

  get firstTrialHint(): string {
    return 'When enabled, the first recorded trial in each qualifying day must be independent for mastery to count.';
  }
}
